import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { ExpenseBankSelect } from "@/components/expenses/ExpenseBankSelect";
import {
  PaymentMethodFieldsGroup,
  emptyPaymentMethodDraft,
  paymentMethodUsesBank,
  paymentReferenceForSubmit,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { installmentRemaining, type SaleInstallmentStatus } from "@/lib/sales/saleInstallments";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import type { SalePaymentMethod } from "@/lib/sales/salesStore";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export type InstallmentPaymentTarget = {
  id: number;
  installment_no: number;
  due_date: string;
  amount: number;
  paid_amount: number;
  penalty_amount?: number;
  penalty_paid?: number;
  remaining_amount?: number;
  remaining_penalty?: number;
  status: SaleInstallmentStatus;
};

export type InstallmentPaymentPayload = {
  amount: number;
  payment_date: string;
  payment_method: SalePaymentMethod;
  bank_id?: number | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  installment: InstallmentPaymentTarget | null;
  saleOutstanding?: number | null;
  onSubmit: (payload: InstallmentPaymentPayload) => Promise<void>;
};

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

export function RecordInstallmentPaymentDialog({
  open,
  onOpenChange,
  installment,
  saleOutstanding,
  onSubmit,
}: Props) {
  const [amountInput, setAmountInput] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [methodDraft, setMethodDraft] = useState<PaymentMethodDraft>(
    emptyPaymentMethodDraft({ paymentMethod: "cash" }),
  );
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = useMemo(
    () => (installment ? installmentRemaining(installment) : 0),
    [installment],
  );

  const maxPayable = useMemo(() => {
    if (saleOutstanding != null && Number.isFinite(Number(saleOutstanding))) {
      return Math.max(0, Math.round(Number(saleOutstanding) * 100) / 100);
    }
    return remaining;
  }, [remaining, saleOutstanding]);

  useEffect(() => {
    if (!open || !installment) return;
    fetchBanks().then(setBanks).catch(() => {});
    setAmountInput(String(remaining));
    const dueDate = installment.due_date?.trim();
    setPaymentDate(dueDate || todayIso());
    setMethodDraft(emptyPaymentMethodDraft({ paymentMethod: "cash" }));
    setSaving(false);
    setError(null);
  }, [open, installment, remaining]);

  const save = async () => {
    if (!installment || saving) return;
    const amount = parseAmount(amountInput);
    if (amount == null) {
      setError("Enter a valid payment amount greater than zero.");
      return;
    }
    if (amount > maxPayable + 0.009) {
      setError(`Payment cannot exceed the outstanding balance of ${maxPayable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`);
      return;
    }
    if (!paymentDate.trim()) {
      setError("Payment date is required.");
      return;
    }
    if (!methodDraft.paymentMethod) {
      setError("Select a payment method.");
      return;
    }
    if (paymentMethodUsesBank(methodDraft.paymentMethod) && !methodDraft.bankId?.trim()) {
      setError("Select a bank for this payment method.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        amount,
        payment_date: paymentDate,
        payment_method: methodDraft.paymentMethod as SalePaymentMethod,
        bank_id: methodDraft.bankId?.trim() ? Number(methodDraft.bankId) : null,
        transaction_reference_no: paymentReferenceForSubmit(methodDraft),
        pay_order_no: methodDraft.paymentMethod === "pay_order" ? methodDraft.payOrderNo?.trim() || null : null,
      });
      onOpenChange(false);
    } catch {
      // Caller shows toast; keep dialog open for correction.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>
            {installment
              ? `Installment #${installment.installment_no} · ${remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} remaining${
                  (installment.remaining_penalty ?? Math.max(0, (installment.penalty_amount ?? 0) - (installment.penalty_paid ?? 0))) > 0.009
                    ? ` (includes late penalty ${(installment.remaining_penalty ?? Math.max(0, (installment.penalty_amount ?? 0) - (installment.penalty_paid ?? 0))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
                    : ""
                }`
              : "Enter payment details."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="installment-payment-amount">Payment amount</Label>
            <Input
              id="installment-payment-amount"
              type="text"
              inputMode="decimal"
              value={amountInput}
              disabled={saving}
              onChange={(e) => {
                setAmountInput(e.target.value);
                setError(null);
              }}
              placeholder="0.00"
            />
            <p className="text-xs text-muted-foreground">
              Defaults to the remaining balance. Overpayments apply to later installments automatically.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Payment date</Label>
            <DatePartsPicker value={paymentDate} onChange={setPaymentDate} disabled={saving} />
          </div>

          <PaymentMethodFieldsGroup
            id="installment-payment-method"
            value={methodDraft}
            onChange={setMethodDraft}
            banks={banks}
            disabled={saving}
            showDetails={false}
            allowNone={false}
            required
            label="Payment method"
            labelClassName="text-xs font-semibold text-foreground"
          />

          {paymentMethodUsesBank(methodDraft.paymentMethod) && (
            <div className="space-y-1.5 min-w-0 w-full">
              <Label className="text-xs font-semibold text-foreground">
                Bank <span className="text-destructive">*</span>
              </Label>
              <ExpenseBankSelect
                value={methodDraft.bankId}
                banks={banks}
                onChange={(v) => setMethodDraft((d) => ({ ...d, bankId: v }))}
                disabled={saving}
                placeholder="Search & select bank"
                triggerClassName="w-full glass-input h-10"
              />
            </div>
          )}

          {methodDraft.paymentMethod === "online" && (
            <div className="space-y-1.5 min-w-0 w-full">
              <Label className="text-xs font-semibold text-foreground">Transaction reference no.</Label>
              <Input
                className="glass-input h-10 w-full"
                value={methodDraft.transactionReferenceNo}
                onChange={(e) => setMethodDraft((d) => ({ ...d, transactionReferenceNo: e.target.value }))}
                placeholder="e.g. TXN-123456"
                disabled={saving}
              />
            </div>
          )}

          {methodDraft.paymentMethod === "cheque" && (
            <div className="space-y-1.5 min-w-0 w-full">
              <Label className="text-xs font-semibold text-foreground">Cheque number</Label>
              <Input
                className="glass-input h-10 w-full"
                value={methodDraft.chequeNo ?? ""}
                onChange={(e) => setMethodDraft((d) => ({ ...d, chequeNo: e.target.value }))}
                placeholder="e.g. 0048291"
                disabled={saving}
              />
            </div>
          )}

          {methodDraft.paymentMethod === "pay_order" && (
            <div className="space-y-1.5 min-w-0 w-full">
              <Label className="text-xs font-semibold text-foreground">Pay order no.</Label>
              <Input
                className="glass-input h-10 w-full"
                value={methodDraft.payOrderNo}
                onChange={(e) => setMethodDraft((d) => ({ ...d, payOrderNo: e.target.value }))}
                placeholder="e.g. PO-98765"
                disabled={saving}
              />
            </div>
          )}

          {methodDraft.paymentMethod === "cash_deposit" && (
            <div className="space-y-1.5 min-w-0 w-full">
              <Label className="text-xs font-semibold text-foreground">Deposit slip no.</Label>
              <Input
                className="glass-input h-10 w-full"
                value={methodDraft.depositSlipNo ?? ""}
                onChange={(e) => setMethodDraft((d) => ({ ...d, depositSlipNo: e.target.value }))}
                placeholder="e.g. DEP-4412"
                disabled={saving}
              />
            </div>
          )}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="premium" onClick={() => void save()} disabled={saving || !installment}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Record payment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
